import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addRecentProject,
  pushBreadcrumb,
} from "../../store/slices/navigationSlice";
import { useServices } from "../../context/ServiceProvider";
import { Alert, Spin, Tabs, Tag } from "antd";
import PreviewOrchestrator from "../preview/PreviewOrchestrator";
import {
  renderGrid,
  setByPathCloned,
  getByPath,
} from "../common/FieldRenderer";
import ProjectObjectivesSection from "../sections/ProjectObjectivesSection";
import ProjectQualStepsSection from "../sections/ProjectQualStepsSection";
import ProjectPagesSection from "../sections/ProjectPagesSection";
import { ListOrdered, Layers, Zap } from "lucide-react";
import { toSalesforceProject } from "../../schema/projectSchema";
import { PICKLIST_LABELS } from "../../config/projectMetadataFields";
import {
  buildInfoFieldsForProject,
  buildRequirementsFieldsForProject,
  buildStatsFieldsForProject,
} from "./projectColumnsConfig";
import TimeframeSelector from "../performance/TimeframeSelector";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";

const ProjectDetails = ({
  setProjectMetadata,
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  showCloneModal: showCloneModalProp,
  setShowCloneModal: setShowCloneModalProp,
  showPreview: showPreviewProp,
  setShowPreview: setShowPreviewProp,
  onHeaderStateChange,
  onRegisterHandlers,
}) => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const location = useLocation();
  const { project, http } = useServices();

  const isControlled =
    setIsEditingProp != null &&
    setShowCloneModalProp != null &&
    setShowPreviewProp != null;

  const [showPreviewLocal, setShowPreviewLocal] = useState(false);
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [showCloneModalLocal, setShowCloneModalLocal] = useState(false);

  const showPreview = isControlled ? showPreviewProp : showPreviewLocal;
  const setShowPreview = isControlled
    ? setShowPreviewProp
    : setShowPreviewLocal;
  const isEditing = isControlled ? isEditingProp : isEditingLocal;
  const setIsEditing = isControlled ? setIsEditingProp : setIsEditingLocal;
  const showCloneModal = isControlled
    ? showCloneModalProp
    : showCloneModalLocal;
  const setShowCloneModal = isControlled
    ? setShowCloneModalProp
    : setShowCloneModalLocal;

  // Local state for data fetching (matching other detail pages pattern)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  // Note: objectives at project level is an object, not an array
  // Array of objectives comes from separate Project Objective API calls

  const [form, setForm] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [previewWidth, setPreviewWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [hideValidationSuccess, setHideValidationSuccess] = useState(false);
  const breadcrumbs = useSelector((state) => state.navigation.breadcrumbs);

  const { picklistMap } = usePicklistMap(Object.values(PICKLIST_LABELS));

  // Report header state to orchestrator for the pure header component
  useEffect(() => {
    if (typeof onHeaderStateChange === "function") {
      onHeaderStateChange({
        saving,
        hasFormErrors: Object.keys(formErrors).length > 0,
        validating,
      });
    }
  }, [saving, formErrors, validating, onHeaderStateChange]);

  const resetEdit = () => {
    setForm(null);
    setFormErrors({});
    setError("");
  };

  // Combine data for backward compatibility
  const projectData = useMemo(() => {
    if (!data) return null;
    setProjectMetadata(data.info);
    return {
      id: data.id || projectId,
      metadata: data.info || {},
      objectives: data.objectives || {}, // objectives is an object at project level
      requirements: data.requirements || {},
      stats: data.stats || {},
      qualificationSteps: data.qualificationSteps || [],
      pages: data.pages || [],
      images: data.images || [],
      pageComponents: data.pageComponents || [],
      settings: data.settings || {},
    };
  }, [data, projectId, setProjectMetadata]);

  // Fetch project data when component mounts or projectId changes
  // Using local state like other detail pages (no caching)
  useEffect(() => {
    console.group("project details render: ", projectId);
    let ignore = false;
    async function load() {
      if (!projectId) return;
      setData(null);
      setLoading(true);
      setApiError("");
      try {
        const record = await project.fetchProject(projectId);
        if (!ignore) {
          setData(record);
        }
      } catch (e) {
        if (!ignore) {
          setApiError(e?.message || "Failed to load project details");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Only re-fetch when projectId changes, not when service reference changes

  // Add to recent projects when data loads
  useEffect(() => {
    if (data) {
      const id = data.id || projectId;
      const name = data.info?.name || "";
      if (id && name) {
        dispatch(addRecentProject({ id, name }));
      }
    }
  }, [data, projectId, dispatch]);

  // Handle preview resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left - 20;
      const newWidth = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        66.67,
      ); // Max 2/3 (66.67%)

      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Push breadcrumb when project loads
  useEffect(() => {
    const id = data?.id || projectId;
    const provided = location?.state?.crumbLabel;
    const fallbackName = data?.info?.name;
    const name = provided || fallbackName;
    if (id && name) {
      if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        dispatch(pushBreadcrumb({ label: "Home", path: "/", type: "home" }));
      }
      dispatch(
        pushBreadcrumb({
          id,
          label: name || `Project${id}`,
          path: `/p/${id}`,
          type: "project",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.info?.name, projectId, location.pathname, projectData]);
  // Requirements and stats are referenced via form/projectData paths in renderGrid
  const qualificationStepsList = useMemo(
    () =>
      Array.isArray(projectData?.qualificationSteps)
        ? projectData.qualificationSteps
        : [],
    [projectData?.qualificationSteps],
  );
  const pagesList = useMemo(
    () => (Array.isArray(projectData?.pages) ? projectData.pages : []),
    [projectData?.pages],
  );

  // Initialize form when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setForm(projectData);
      setFormErrors({});
      setSuccess("");
    }
  }, [isEditing, projectData]);

  function validateEditForm(current) {
    const errs = {};
    const name = current?.metadata?.name || "";
    if (!name || String(name).trim().length < 3) {
      errs["metadata.name"] = "Name is required (min 3 characters)";
    }
    const shortName = current?.metadata?.shortName || "";
    if (shortName && String(shortName).length < 2) {
      errs["metadata.shortName"] = "Short Name must be at least 2 characters";
    }
    const status = current?.metadata?.projectStatus || "";
    if (
      status &&
      !["Draft", "Open", "Roster Hold", "Closed"].includes(status)
    ) {
      errs["metadata.projectStatus"] = "Invalid status";
    }
    const start = current?.metadata?.hireStartDate;
    const end = current?.metadata?.predictedCloseDate;
    if (start && end) {
      const sd = new Date(start);
      const ed = new Date(end);
      if (!isNaN(sd) && !isNaN(ed) && ed < sd) {
        errs["metadata.predictedCloseDate"] =
          "Close date must be on/after start date";
      }
    }
    const account = current?.metadata?.account || "";
    if (account) {
      const validId = /^[A-Za-z0-9]{15,18}$/;
      if (!validId.test(String(account))) {
        errs["metadata.account"] = "Account must be a 15/18 char Salesforce Id";
      }
    }
    // Add required checks for fields that did not previously have presence validation
    const requiredPaths = [
      "metadata.name",
      "metadata.shortName",
      "objectives.contributorFacingName",
      "metadata.appenPartner",
      "metadata.account",
      "metadata.hireStartDate",
      "metadata.predictedCloseDate",
      "metadata.projectStatus",
      "metadata.projectType",
      "metadata.projectPriority",
    ];
    requiredPaths.forEach((path) => {
      const v = getByPath(current, path);
      if (v === undefined || String(v).trim() === "") {
        if (!errs[path]) errs[path] = "This field is required";
      }
    });
    return errs;
  }

  function handleEditChange(path, value, labelPath, newLabel) {
    setForm((prev) => {
      let next = setByPathCloned(prev || {}, path, value);
      if (labelPath && newLabel !== undefined) {
        next = setByPathCloned(next, labelPath, newLabel);
      }
      setFormErrors(validateEditForm(next));
      return next;
    });
  }

  const validationGroups = useMemo(() => {
    const alerts = Array.isArray(validation?.alerts) ? validation.alerts : [];
    const groups = { error: [], warning: [], info: [] };
    for (const a of alerts) {
      const t = (a?.type === "warn" ? "warning" : a?.type) || "info";
      if (t === "error" || t === "warning" || t === "info") {
        groups[t].push(a);
      } else {
        groups.info.push(a);
      }
    }
    return groups;
  }, [validation]);

  async function handleValidate() {
    const id = projectId || projectData?.id;
    if (!id) {
      setValidationError("Missing project id");
      return;
    }
    setValidationError("");
    setValidating(true);
    setSuccess("");
    setHideValidationSuccess(false);
    try {
      const resp = await http.sfGet(`/Validate`, {
        id: String(id),
        type: "Project",
      });
      if (resp == null) {
        setValidation(null);
        return;
      }
      const successFlag = resp?.success === true;
      const alerts = Array.isArray(resp?.alerts) ? resp.alerts : [];
      setValidation({ success: successFlag, alerts });
    } catch (e) {
      const details = e?.details;
      if (details && typeof details === "object") {
        const alerts = Array.isArray(details.alerts) ? details.alerts : [];
        setValidation({ success: !!details.success, alerts });
      } else {
        setValidation(null);
        setValidationError(e?.message || "Validation failed");
      }
    } finally {
      setValidating(false);
    }
  }

  async function handleSave() {
    if (!form) return;
    const errs = validateEditForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const id = projectId || projectData?.id;
    if (!id) {
      setError("Missing project id");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Compute minimal PATCH payload by diffing SF-mapped records
      const toSfInput = (obj) =>
        obj ? { ...obj, info: obj.metadata ?? obj.info } : {};
      const originalSf = toSalesforceProject(toSfInput(data)); // use raw "data", not projectData
      const updatedSf = toSalesforceProject(toSfInput(form));
      const payload = {};
      const keys = Object.keys(updatedSf);
      keys.forEach((k) => {
        const a = originalSf[k];
        const b = updatedSf[k];
        const bothUndefined = a === undefined && b === undefined;
        if (bothUndefined) return;
        if (a !== b) {
          payload[k] = b === undefined ? null : b;
        }
      });
      // Ensure we don't send empty payloads

      console.log("Original SF Object: ", originalSf);
      console.log("Data for Original: ", projectData);
      console.log("Updated SF Object: ", updatedSf);
      console.log("Data for Updated: ", form);
      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        setSuccess("No changes to update");
        return;
      }
      console.log("Payload : ", payload);
      await project.patchProject(String(id), payload);

      // Refetch project data - no caching
      const fresh = await project.fetchProject(String(id));
      setData(fresh);

      setIsEditing(false);
      setSuccess("Project updated successfully");
    } catch (e) {
      setError(e?.message || "Failed to update project");
    } finally {
      setSaving(false);
    }
  }

  if (typeof onRegisterHandlers === "function") {
    onRegisterHandlers(() => ({
      handleSave,
      handleValidate,
      resetEdit,
    }));
  }

  useEffect(() => {
    if (!location?.pathname?.startsWith("/p/")) return;

    // unique per navigation entry; falls back to path+id if key missing
    const navKey =
      location.key ||
      `${location.pathname}:${projectId || projectData?.id || ""}`;
    const flag = `RELOADED::${navKey}`;

    if (!sessionStorage.getItem(flag)) {
      sessionStorage.setItem(flag, "1");
      // defer so prior effects can finish
      setTimeout(() => window.location.reload(), 0);
    }
  }, [location.key, location.pathname, projectId, projectData?.id]);

  useEffect(() => {
    console.log("projectData", projectData);
  }, [projectData]);
  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {loading && (
          <div className="mb-6">
            <Spin fullscreen tip="Loading project…" />
          </div>
        )}
        {error && (
          <div className="mb-6">
            <Alert type="error" showIcon message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert type="success" showIcon message={success} />
          </div>
        )}
        {validationError && (
          <div className="mb-6">
            <Alert type="error" showIcon message={validationError} />
          </div>
        )}
        {validation && (
          <div className="mb-6 space-y-4">
            {Array.isArray(validation.alerts) &&
            validation.alerts.length === 0 ? (
              !hideValidationSuccess ? (
                <div
                  className="fade-out-after-3s"
                  onAnimationEnd={() => setHideValidationSuccess(true)}
                >
                  <Alert
                    type="success"
                    showIcon
                    message="Validation Successful"
                    className="border-green-200 bg-green-50"
                  />
                </div>
              ) : null
            ) : (
              <div className="space-y-4">
                {validationGroups.error.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Validation Errors (${validationGroups.error.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.error.map((a, idx) => (
                          <li key={`err-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-red-200 bg-red-50"
                  />
                )}
                {validationGroups.warning.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Warnings (${validationGroups.warning.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.warning.map((a, idx) => (
                          <li key={`warn-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-yellow-200 bg-yellow-50"
                  />
                )}
                {validationGroups.info.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`Information (${validationGroups.info.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.info.map((a, idx) => (
                          <li key={`info-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-blue-200 bg-blue-50"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* High-level summary cards */}
        {/* {projectData?.id ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/70 backdrop-blur-sm p-4 rounded-lg border border-white/30 hover:border-white/50 transition-all duration-200 hover:shadow-md">
              <div className="text-xs uppercase text-gray-500 mb-1">
                Project
              </div>
              <div className="text-lg font-semibold text-gray-900 truncate">
                {projectData?.metadata?.name || "—"}
              </div>
              <div className="text-sm text-gray-600 truncate">
                {projectData?.metadata?.shortName || ""}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Status:{" "}
                <span className="text-gray-800">
                  <Tag
                    bordered={false}
                    color={
                      projectData?.metadata?.projectStatus === "Open"
                        ? "success"
                        : projectData?.metadata?.projectStatus === "Closed"
                          ? "error"
                          : "processing"
                    }
                  >
                    {projectData?.metadata?.projectStatus || "—"}
                  </Tag>
                </span>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm p-4 rounded-lg border border-white/30 hover:border-white/50 transition-all duration-200 hover:shadow-md">
              <div className="text-xs uppercase text-gray-500 mb-1">
                Objective
              </div>
              <div className="text-lg font-semibold text-gray-900 truncate">
                {projectData?.objectives?.objectiveName || "—"}
              </div>
              <div className="text-sm text-gray-600 truncate">
                {projectData?.objectives?.contributorFacingName || ""}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Work Type:{" "}
                <span className="text-gray-800">
                  {projectData?.objectives?.workType || "—"}
                </span>
              </div>
            </div>

            <div className="bg-white/70 backdrop-blur-sm p-4 rounded-lg border border-white/30 hover:border-white/50 transition-all duration-200 hover:shadow-md">
              <div className="text-xs uppercase text-gray-500 mb-1">Counts</div>
              <div className="flex items-center gap-6 text-sm text-gray-700">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4 text-primary-600" />
                  <span>{qualificationStepsList.length} steps</span>
                </div>
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-primary-600" />
                  <span>{pagesList.length} pages</span>
                </div>
              </div>
            </div>
          </div>
        ) : null} */}

        {projectData?.id && (
          <div
            ref={containerRef}
            className={`${showPreview ? "flex gap-4" : "max-w-6xl mx-auto"} ${
              isDragging ? "select-none cursor-col-resize" : ""
            }`}
          >
            <div
              className={`${
                showPreview ? `flex-shrink-0` : ""
              } h-min bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20`}
              style={{
                width: showPreview ? `${previewWidth}%` : "100%",
              }}
            >
              <div className="p-6">
                {/* Project core details, requirements, and stats */}
                <Tabs
                  defaultActiveKey="info"
                  items={[
                    {
                      key: "info",
                      label: "Info",
                      children: renderGrid(
                        resolvePicklistFields(
                          [
                            {
                              label: "Salesforce Id",
                              value: projectData?.id || "—",
                              readOnly: true,
                            },
                            ...buildInfoFieldsForProject({
                              form,
                              data: projectData,
                            }),
                          ],
                          picklistMap,
                        ),
                        {
                          editing: isEditing,
                          form: form || projectData,
                          onChange: handleEditChange,
                          errors: formErrors,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: projectData,
                        },
                      ),
                    },
                    {
                      key: "requirements",
                      label: "Requirements",
                      children: renderGrid(
                        buildRequirementsFieldsForProject(),
                        {
                          editing: isEditing,
                          form: form || projectData,
                          onChange: handleEditChange,
                          errors: formErrors,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: projectData,
                        },
                      ),
                    },
                    {
                      key: "stats",
                      label: "Stats",
                      children: renderGrid(buildStatsFieldsForProject(), {
                        editing: isEditing,
                        form: form || projectData,
                        onChange: handleEditChange,
                        errors: formErrors,
                        onEnableEdit: () => setIsEditing(true),
                        originalData: projectData,
                      }),
                    },
                  ]}
                />

                <div className="space-y-4">
                  {/* Project Objectives Section */}
                  <ProjectObjectivesSection
                    projectId={projectData?.id || projectId}
                    projectName={
                      projectData?.metadata?.name || form?.metadata?.name
                    }
                    startDate={projectData?.metadata?.hireStartDate}
                    endDate={projectData?.metadata?.predictedCloseDate}
                  />

                  {/* Qualification Steps (API) Section */}
                  <ProjectQualStepsSection
                    projectId={projectData?.id || projectId}
                    projectName={
                      projectData?.metadata?.name || form?.metadata?.name
                    }
                  />

                  {/* Project Pages (API) Section */}
                  <ProjectPagesSection
                    projectId={projectData?.id || projectId}
                    projectName={
                      projectData?.metadata?.name || form?.metadata?.name
                    }
                  />
                </div>
              </div>
            </div>
            {showPreview && (
              <>
                {/* Drag Handle */}
                <div
                  className="w-2 cursor-col-resize flex-shrink-0 transition-colors duration-200 relative group"
                  onMouseDown={() => setIsDragging(true)}
                >
                  <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bg-gray-300 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-6 rounded-full group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <div
                  className="flex-shrink-0"
                  style={{ width: `calc(${100 - previewWidth}% - 40px)` }}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Live Preview
                      </h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Zap size={16} className="mr-2" />
                        Real-time rendering
                      </div>
                    </div>
                    <div className="rounded-lg">
                      <PreviewOrchestrator
                        objectType="QualSteps"
                        id={projectData?.id}
                        projectId={projectData?.id}
                        payload={{}}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
